import React, { useState } from 'react';
import {
  Box, List, ListItem, ListItemText, TextField, InputAdornment,
  Chip, Typography, IconButton, Stack, Button, Divider, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Menu, MenuItem as DropdownItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Event as EventIcon,
  MoreVert as MoreIcon,
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import {
  format, parseISO, isToday, isFuture, isPast,
} from 'date-fns';

function AppointmentList({
  appointments = [],
  loading = false,
  onAppointmentSelect,
  onAppointmentEdit,
  onAppointmentDelete,
  onAddAppointment,
  selectedAppointment = null,
}) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleMenuOpen = (event, appointment) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedItem(appointment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleEdit = () => {
    if (selectedItem) onAppointmentEdit(selectedItem);
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedItem) onAppointmentDelete(selectedItem);
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled': return 'primary';
      case 'Confirmed': return 'success';
      case 'In Progress': return 'info';
      case 'Completed': return 'default';
      case 'Cancelled': return 'error';
      case 'No Show': return 'warning';
      default: return 'default';
    }
  };

  const getTimeStatus = (date) => {
    try {
      const appointmentDate = parseISO(date);
      if (isToday(appointmentDate)) return 'Today';
      if (isFuture(appointmentDate)) return 'Upcoming';
      if (isPast(appointmentDate)) return 'Past';
    } catch {
      return '';
    }
    return '';
  };

  const filteredAppointments = appointments.filter((appt) => {
    const matchesSearch = search === ''
      || appt.patientName?.toLowerCase().includes(search.toLowerCase())
      || appt.appointmentType?.toLowerCase().includes(search.toLowerCase())
      || appt.provider?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus = statusFilter === 'all'
      || appt.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <EventIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Appointments
            </Typography>
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddAppointment}
            size="small"
          >
            Add
          </Button>
        </Stack>

        <TextField
          placeholder="Search appointments..."
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="all">All Appointments</MenuItem>
            <MenuItem value="Scheduled">Scheduled</MenuItem>
            <MenuItem value="Confirmed">Confirmed</MenuItem>
            <MenuItem value="In Progress">In Progress</MenuItem>
            <MenuItem value="Completed">Completed</MenuItem>
            <MenuItem value="Cancelled">Cancelled</MenuItem>
            <MenuItem value="No Show">No Show</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {filteredAppointments.length}
          {' '}
          appointment
          {filteredAppointments.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredAppointments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
            <EventIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              {appointments.length === 0 ? 'No appointments yet' : 'No appointments found'}
            </Typography>
            {appointments.length === 0 && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onAddAppointment}
                sx={{ mt: 2 }}
              >
                Add First Appointment
              </Button>
            )}
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {filteredAppointments.map((appointment, index) => (
              <React.Fragment key={appointment._id}>
                <ListItem
                  button
                  selected={selectedAppointment?._id === appointment._id}
                  onClick={() => onAppointmentSelect(appointment)}
                  sx={{
                    '&:hover': { bgcolor: 'action.hover' },
                    '&.Mui-selected': {
                      bgcolor: 'primary.lighter',
                      '&:hover': { bgcolor: 'primary.light' },
                    },
                  }}
                  secondaryAction={(
                    <IconButton edge="end" onClick={(e) => handleMenuOpen(e, appointment)}>
                      <MoreIcon />
                    </IconButton>
                  )}
                >
                  <ListItemText
                    primary={(
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Typography variant="subtitle2" fontWeight={700}>
                          {appointment.patientName || 'Unknown Patient'}
                        </Typography>
                        <Chip
                          label={appointment.status}
                          size="small"
                          color={getStatusColor(appointment.status)}
                          sx={{ height: 20 }}
                        />
                        {getTimeStatus(appointment.date) && (
                          <Chip
                            label={getTimeStatus(appointment.date)}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20 }}
                          />
                        )}
                      </Stack>
                    )}
                    secondary={(
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <CalendarIcon sx={{ fontSize: 14 }} />
                          <Typography variant="caption">
                            {format(parseISO(appointment.date), 'MMM dd, yyyy')}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <TimeIcon sx={{ fontSize: 14 }} />
                          <Typography variant="caption">
                            {appointment.startTime}
                            {' '}
                            -
                            {appointment.endTime}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {appointment.appointmentType}
                          {' '}
                          •
                          {appointment.provider}
                        </Typography>
                      </Stack>
                    )}
                  />
                </ListItem>
                {index < filteredAppointments.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <DropdownItem onClick={handleEdit}>Edit Appointment</DropdownItem>
        <DropdownItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          Delete Appointment
        </DropdownItem>
      </Menu>
    </Box>
  );
}

export default AppointmentList;
